import React from "react"

import { Box, ToggleButtonGroup, } from "@mui/material"

import { ErrorResponse, Loading, RestResponse } from "@sinossi/mates-react-library"

import { t, Trans, } from "@lingui/macro"

import { PageHeader, PageSectionTitle, RoutesData, SbToggleButton, } from "components/commons"
import withUseRouter, { UseRouterParams } from "services/router"
import { MatesComponent, MatesComponentState, } from "components/mates-component"

import { ContractTable } from "components/contract/contract-table/contract-table"
import { ContractAdvancedSearch } from "components/contract/advanced-searchbar"
import CreateContractModal from "../../components/contract/create-contract-modal";
import {
    ContractRequest,
    ContractResponse,
    CreateContract, CreateContractWithId,
    CreateSpecificWorkflow,
    SearchFilter,
    WorkflowObjectType
} from "../../models";
import { ContractServices } from "../../services/contract.services";
import { DeleteContractModal } from "../../components/contract/delete-contract-modal";
import { WorkflowService } from "../../services/workflow.service";
import { SbSnackbar } from "../../components/commons/sb-snackbar";

interface ContractProps extends UseRouterParams {
}
interface ContractState extends MatesComponentState {
    loading: boolean
    activeSection: string | null
    openCreate: boolean
    openDelete: boolean
    data: ContractResponse | null
    contractId: string
    request: ContractRequest
    newContractId: string
    templateId: string
    snackbarData: {
        openNotification: boolean,
        notificationMessage: string,
        severity: 'error' | 'info' | 'success' | 'warning'
    }
}

class Contract extends React.Component<ContractProps, ContractState> {
    private readonly matesComponent: MatesComponent<ContractProps, ContractState> = new MatesComponent(this)

    constructor(props: any) {
        super(props)
        this.state = {
            ...this.matesComponent.setupState(),
            loading: true,
            activeSection: "contracts",
            openCreate: false,
            openDelete: false,
            data: null,
            contractId: "",
            request: {
                pageSize: 10,
                pageId: 0,
                orderType: null,
                orderBy: null,
                contractNumber: null,
                statusList: ["ACTIVE", "NEGOTIATING"],
                brandId: null,
                assetId: null,
                minStartDate: null,
                maxStartDate: null
            },
            newContractId: "",
            templateId: "",
            snackbarData: {
                openNotification: false,
                notificationMessage: "",
                severity: "info"
            }
        }
    }

    componentDidMount() {
        this.getContracts()
    }

    private getContracts = () => {
        let request: ContractRequest = this.state.request

        if (this.state.activeSection === "contracts") {
            request = {
                ...this.state.request,
                statusList: ["ACTIVE", "NEGOTIATING", "CREATED"]
            }
        }
        else if (this.state.activeSection === "archive") {
            request = {
                ...this.state.request,
                statusList: ["CLOSED", "REJECTED"]
            }
        }

        this.setState(() => ({
            request: request
        }), () => ContractServices.getFilteredContracts(request, this.getContractsCallback, this.matesComponent.onError))
    }

    private getContractsCallback = (response: RestResponse<ContractResponse, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        if (response.data) {
            this.setState(() => ({
                data: response.data,
                loading: false
            }))
        }
    }

    private changeSection = (_event: React.MouseEvent<HTMLElement>, section: string | null) => {
        this.setState(() => ({ activeSection: section }), this.getContracts)
    }

    private openModalCreate = () => {
        this.setState(() => ({
            openCreate: true,
            contractId: ""
        }))
    }

    private closeModalCreate = () => {
        this.setState(() => ({
            openCreate: false
        }))
    }

    private openModalDelete = (contractId: string) => {
        this.setState(() => ({
            openDelete: true,
            contractId: contractId
        }))
    }

    private closeModalDelete = () => {
        this.setState(() => ({
            openDelete: false
        }))
    }

    private openModalUpdate = (contractId: string) => {
        this.setState(() => ({
            openCreate: true,
            contractId: contractId
        }))
    }

    private deleteContract = (contractId: string) => {
        this.setState(() => ({
            loading: true
        }), () => ContractServices.deleteContract(contractId, this.editContractCallback, this.matesComponent.onError))

        this.closeModalDelete()
    }

    private submit = (requestWithId: CreateContractWithId, templateId?: string) => {
        let requestWithoutId: CreateContract = (({ id, ...rest }) => rest)(requestWithId)
        if (requestWithId.id !== "") {
            ContractServices.updateContract(requestWithId.id, requestWithoutId, this.editContractCallback, this.matesComponent.onError)
        }
        else {
            this.setState(() => ({
                templateId: templateId ?? ""
            }), () => ContractServices.createContract(requestWithoutId, this.createContractCallback, this.matesComponent.onError)
            )
        }
        this.closeModalCreate()
    }

    private editContractCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.setState(() => ({ loading: false }))
            this.matesComponent.onError(response)
            return
        }

        this.getContracts()
    }

    private createContractCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            this.setState(() => ({ loading: false }))
            return
        }

        if (this.state.templateId !== "") {
            this.setState(() => ({
                newContractId: response.data.id
            }), this.createWorkflow)
        }
        else {
            this.getContracts()
        }
    }

    private createWorkflow = () => {
        const request: CreateSpecificWorkflow = {
            objectId: this.state.newContractId,
            objectType: WorkflowObjectType.CONTRACT,
            workflowTemplateId: this.state.templateId
        }

        WorkflowService.createWorkflowFromSpecificObject(request, this.createWorkflowCallback, this.matesComponent.onError)
    }

    private createWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            this.setState(() => ({ loading: false }))
            this.handleOpenNotification(
                t({ id: "contract.create-workflow.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
                "error"
            )
            return
        }
        this.handleOpenNotification(
            t({ id: "contract.create-workflow.success", message: "Workflow creato con successo." }),
            "success"
        )
        this.getContracts()
    }

    private handlePageChange = (pagination: SearchFilter) => {
        let request: ContractRequest = {
            ...this.state.request,
            orderType: pagination.orderType,
            orderBy: pagination.orderBy,
            pageSize: pagination.pageSize,
            pageId: pagination.pageId
        }

        this.setState(() => ({
            request: request,
            loading: true
        }), this.getContracts)
    }

    private search = (request: ContractRequest) => {
        this.setState(() => ({
            request: request,
            loading: true
        }), this.getContracts)
    }

    private handleOpenNotification = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
        this.setState(() => ({
            snackbarData: {
                openNotification: true,
                notificationMessage: message,
                severity: severity
            }
        }))
    }

    private handleCloseNotification = () => {
        this.setState(() => ({
            snackbarData: {
                ...this.state.snackbarData,
                openNotification: false
            }
        }))
    }

    render() {
        return (
            <Box sx={{ width: '100%', pt: 2, position: "relative" }}>
                <Loading show={this.state.loading} />
                <PageHeader
                    title={RoutesData.CONTRACT.title}
                    paths={[RoutesData.DASHBOARD, RoutesData.CONTRACT]}
                    addButtonModal={"Aggiungi Contratto"}
                    onClickButtonModal={this.openModalCreate}
                />

                <ContractAdvancedSearch onSearch={this.search} />

                {!this.state.loading && <>
                    <Box sx={{ mt: 3 }}>
                        <PageSectionTitle title={t({ id: "contract-opening-title-table", message: "Aperture" })}>
                            <ToggleButtonGroup
                                value={this.state.activeSection}
                                exclusive
                                onChange={this.changeSection}
                            >
                                <SbToggleButton value="contracts">
                                    <Trans id="contract-table-select-opening">
                                        Aperture
                                    </Trans>
                                </SbToggleButton>
                                <SbToggleButton value="archive">
                                    <Trans id="contract-table-select-archive">
                                        Archivio
                                    </Trans>
                                </SbToggleButton>
                            </ToggleButtonGroup>
                        </PageSectionTitle>

                        {
                            this.state.data &&
                            <ContractTable
                                data={this.state.data.items}
                                handleCallback={this.handlePageChange}
                                openModal={this.openModalUpdate}
                                openDelete={this.openModalDelete}
                                pagination={this.state.data.pagination}
                            />
                        }
                    </Box>
                </>}
                {
                    this.state.openCreate ?
                        <CreateContractModal onClose={this.closeModalCreate} submit={this.submit} contractId={this.state.contractId} />
                        :
                        null
                }
                {
                    this.state.openDelete &&
                    <DeleteContractModal onClose={this.closeModalDelete} contractId={this.state.contractId} delete={this.deleteContract} />
                }
                <SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />
            </Box >
        )
    }
}

export default withUseRouter(Contract)
